/**
 * The Bible's story in order, for Before or After.
 *
 * ── What "order" means here ──────────────────────────────────────────────────
 *
 * This is the order events come in **the Bible's own narrative**, not a
 * reconstruction of history. That distinction is doing real work: the dates of
 * the patriarchs are traditional rather than attested, the exodus is placed two
 * centuries apart by different scholars, and the first eleven chapters of
 * Genesis are not dated by anybody. A game that claimed to know when the flood
 * happened would be teaching something false in order to keep score.
 *
 * So the question the game asks is "which comes first in the story", which has
 * a definite answer, is the thing a reader actually learns, and is honest.
 *
 * `when` is shown after the answer. Where a date is well attested it is given
 * plainly; where it is disputed or traditional, it says so. Several events have
 * no date at all, and say that too.
 *
 * ── Adding an event ──────────────────────────────────────────────────────────
 *
 * Insert it in the right place; `order` is derived from position, so there is
 * no numbering to renumber. `npm run check:timeline` verifies the file.
 */

/**
 * @typedef {object} Event
 * @property {string} id
 * @property {string} title   short enough to read at a glance
 * @property {string} where   book or setting, for the card's second line
 * @property {string} when    shown after answering; honest about uncertainty
 * @property {'genesis'|'patriarchs'|'exodus'|'land'|'kings'|'exile'|'return'|'gospels'|'church'} era
 */

/** @type {Event[]} */
const SEQUENCE = [
  // ── Before the nation ─────────────────────────────────────────────────────
  { id: 'creation', title: 'Creation', where: 'Genesis 1', when: 'Undated — before history begins', era: 'genesis' },
  { id: 'fall', title: 'The fall', where: 'Genesis 3', when: 'Undated', era: 'genesis' },
  { id: 'cain', title: 'Cain and Abel', where: 'Genesis 4', when: 'Undated', era: 'genesis' },
  { id: 'flood', title: 'The flood', where: 'Genesis 6–9', when: 'Undated', era: 'genesis' },
  { id: 'babel', title: 'The tower of Babel', where: 'Genesis 11', when: 'Undated', era: 'genesis' },

  // ── The patriarchs ────────────────────────────────────────────────────────
  { id: 'abram-called', title: 'Abram is called', where: 'Genesis 12', when: 'c. 2000 BC — traditional, not attested', era: 'patriarchs' },
  { id: 'isaac-born', title: 'Isaac is born', where: 'Genesis 21', when: 'c. 1900 BC — traditional', era: 'patriarchs' },
  { id: 'moriah', title: 'The binding of Isaac', where: 'Genesis 22', when: 'c. 1880 BC — traditional', era: 'patriarchs' },
  { id: 'jacob-esau', title: 'Jacob takes the birthright', where: 'Genesis 25–27', when: 'c. 1850 BC — traditional', era: 'patriarchs' },
  { id: 'jabbok', title: 'Jacob wrestles till dawn', where: 'Genesis 32', when: 'c. 1820 BC — traditional', era: 'patriarchs' },
  { id: 'joseph-sold', title: 'Joseph is sold into Egypt', where: 'Genesis 37', when: 'c. 1800 BC — traditional', era: 'patriarchs' },
  { id: 'joseph-rules', title: 'Joseph rules Egypt', where: 'Genesis 41', when: 'c. 1780 BC — traditional', era: 'patriarchs' },

  // ── Out of Egypt ──────────────────────────────────────────────────────────
  { id: 'slavery', title: 'Israel enslaved in Egypt', where: 'Exodus 1', when: 'Several centuries; no fixed date', era: 'exodus' },
  { id: 'moses-born', title: 'Moses is born', where: 'Exodus 2', when: 'Disputed — 15th or 13th century BC', era: 'exodus' },
  { id: 'bush', title: 'The burning bush', where: 'Exodus 3', when: 'Disputed — 15th or 13th century BC', era: 'exodus' },
  { id: 'passover', title: 'The first Passover', where: 'Exodus 12', when: 'Disputed — 15th or 13th century BC', era: 'exodus' },
  { id: 'sea', title: 'Crossing the sea', where: 'Exodus 14', when: 'Disputed — 15th or 13th century BC', era: 'exodus' },
  { id: 'sinai', title: 'The law given at Sinai', where: 'Exodus 20', when: 'Weeks after the sea', era: 'exodus' },
  { id: 'calf', title: 'The golden calf', where: 'Exodus 32', when: 'Weeks after Sinai', era: 'exodus' },
  { id: 'spies', title: 'The twelve spies turn back', where: 'Numbers 13–14', when: 'About two years after Egypt', era: 'exodus' },
  { id: 'wilderness', title: 'Forty years in the wilderness', where: 'Numbers', when: 'A generation', era: 'exodus' },
  { id: 'moses-dies', title: 'Moses dies within sight of the land', where: 'Deuteronomy 34', when: 'End of the forty years', era: 'exodus' },

  // ── The land ──────────────────────────────────────────────────────────────
  { id: 'jordan', title: 'Crossing the Jordan', where: 'Joshua 3', when: 'Disputed — 14th or 12th century BC', era: 'land' },
  { id: 'jericho', title: 'The walls of Jericho', where: 'Joshua 6', when: 'Days after the crossing', era: 'land' },
  { id: 'judges', title: 'The age of the judges begins', where: 'Judges 2', when: 'c. 1200–1050 BC', era: 'land' },
  { id: 'deborah', title: 'Deborah judges Israel', where: 'Judges 4', when: 'c. 1150 BC — approximate', era: 'land' },
  { id: 'gideon', title: 'Gideon and the three hundred', where: 'Judges 7', when: 'c. 1120 BC — approximate', era: 'land' },
  { id: 'samson', title: 'Samson and the Philistines', where: 'Judges 13–16', when: 'c. 1080 BC — approximate', era: 'land' },
  { id: 'ruth', title: 'Ruth gleans in Boaz’s field', where: 'Ruth 2', when: 'During the judges', era: 'land' },

  // ── The kings ─────────────────────────────────────────────────────────────
  { id: 'saul', title: 'Saul anointed as first king', where: '1 Samuel 10', when: 'c. 1050 BC', era: 'kings' },
  { id: 'goliath', title: 'David and Goliath', where: '1 Samuel 17', when: 'c. 1025 BC', era: 'kings' },
  { id: 'david-king', title: 'David becomes king', where: '2 Samuel 5', when: 'c. 1010 BC', era: 'kings' },
  { id: 'bathsheba', title: 'David and Bathsheba', where: '2 Samuel 11', when: 'c. 990 BC', era: 'kings' },
  { id: 'temple', title: 'Solomon builds the temple', where: '1 Kings 6', when: 'c. 960 BC', era: 'kings' },
  { id: 'divided', title: 'The kingdom splits in two', where: '1 Kings 12', when: 'c. 930 BC', era: 'kings' },
  { id: 'carmel', title: 'Elijah on Mount Carmel', where: '1 Kings 18', when: 'c. 860 BC', era: 'kings' },
  { id: 'elisha', title: 'Elisha succeeds Elijah', where: '2 Kings 2', when: 'c. 850 BC', era: 'kings' },
  { id: 'jonah', title: 'Jonah sent to Nineveh', where: 'Jonah 1', when: 'c. 780 BC — approximate', era: 'kings' },
  { id: 'isaiah-call', title: 'Isaiah sees the Lord in the temple', where: 'Isaiah 6', when: '740 BC', era: 'kings' },
  { id: 'samaria', title: 'Assyria destroys Samaria', where: '2 Kings 17', when: '722 BC', era: 'kings' },
  { id: 'sennacherib', title: 'Assyria besieges Jerusalem', where: '2 Kings 18–19', when: '701 BC', era: 'kings' },
  { id: 'josiah', title: 'Josiah finds the book of the law', where: '2 Kings 22', when: '622 BC', era: 'kings' },

  // ── Exile ─────────────────────────────────────────────────────────────────
  { id: 'jeremiah', title: 'Jeremiah warns Jerusalem', where: 'Jeremiah', when: 'c. 627–586 BC', era: 'exile' },
  { id: 'daniel-taken', title: 'Daniel taken to Babylon', where: 'Daniel 1', when: '605 BC', era: 'exile' },
  { id: 'jerusalem-falls', title: 'Jerusalem falls; the temple burns', where: '2 Kings 25', when: '587 BC', era: 'exile' },
  { id: 'ezekiel', title: 'Ezekiel prophesies in exile', where: 'Ezekiel', when: 'c. 593–571 BC', era: 'exile' },
  { id: 'furnace', title: 'The fiery furnace', where: 'Daniel 3', when: '6th century BC', era: 'exile' },
  { id: 'lions', title: 'Daniel in the lions’ den', where: 'Daniel 6', when: 'c. 539 BC', era: 'exile' },

  // ── Return ────────────────────────────────────────────────────────────────
  { id: 'cyrus', title: 'Cyrus lets the exiles go home', where: 'Ezra 1', when: '538 BC', era: 'return' },
  { id: 'second-temple', title: 'The second temple is finished', where: 'Ezra 6', when: '516 BC', era: 'return' },
  { id: 'esther', title: 'Esther goes before the king', where: 'Esther 5', when: 'c. 479 BC', era: 'return' },
  { id: 'ezra', title: 'Ezra reads the law aloud', where: 'Nehemiah 8', when: 'c. 445 BC', era: 'return' },
  { id: 'walls', title: 'Nehemiah rebuilds the walls', where: 'Nehemiah 6', when: '445 BC', era: 'return' },
  { id: 'malachi', title: 'The last prophet of the Old Testament', where: 'Malachi', when: 'c. 430 BC', era: 'return' },

  // ── The gospels ───────────────────────────────────────────────────────────
  { id: 'john-born', title: 'John the Baptist is born', where: 'Luke 1', when: 'c. 5 BC', era: 'gospels' },
  { id: 'nativity', title: 'Jesus is born', where: 'Luke 2', when: 'c. 5–4 BC', era: 'gospels' },
  { id: 'temple-boy', title: 'Jesus at twelve in the temple', where: 'Luke 2', when: 'c. AD 8', era: 'gospels' },
  { id: 'baptism', title: 'Jesus is baptised', where: 'Matthew 3', when: 'c. AD 29', era: 'gospels' },
  { id: 'sermon', title: 'The Sermon on the Mount', where: 'Matthew 5–7', when: 'c. AD 30', era: 'gospels' },
  { id: 'five-thousand', title: 'Five thousand are fed', where: 'Mark 6', when: 'c. AD 31', era: 'gospels' },
  { id: 'transfiguration', title: 'The transfiguration', where: 'Matthew 17', when: 'c. AD 32', era: 'gospels' },
  { id: 'lazarus', title: 'Lazarus is raised', where: 'John 11', when: 'c. AD 32', era: 'gospels' },
  { id: 'entry', title: 'Riding into Jerusalem', where: 'Matthew 21', when: 'Days before the crucifixion', era: 'gospels' },
  { id: 'supper', title: 'The last supper', where: 'Luke 22', when: 'The night before', era: 'gospels' },
  { id: 'cross', title: 'The crucifixion', where: 'John 19', when: 'c. AD 30–33', era: 'gospels' },
  { id: 'risen', title: 'The resurrection', where: 'Matthew 28', when: 'Three days later', era: 'gospels' },
  { id: 'ascension', title: 'The ascension', where: 'Acts 1', when: 'Forty days later', era: 'gospels' },

  // ── The church ────────────────────────────────────────────────────────────
  { id: 'pentecost', title: 'Pentecost', where: 'Acts 2', when: 'Ten days after the ascension', era: 'church' },
  { id: 'stephen', title: 'Stephen is stoned', where: 'Acts 7', when: 'c. AD 34', era: 'church' },
  { id: 'damascus', title: 'Saul is blinded on the Damascus road', where: 'Acts 9', when: 'c. AD 34', era: 'church' },
  { id: 'cornelius', title: 'The gospel goes to the gentiles', where: 'Acts 10', when: 'c. AD 40', era: 'church' },
  { id: 'council', title: 'The council of Jerusalem', where: 'Acts 15', when: 'c. AD 49', era: 'church' },
  { id: 'corinth', title: 'Paul writes to Corinth', where: '1 Corinthians', when: 'c. AD 55', era: 'church' },
  { id: 'rome', title: 'Paul reaches Rome in chains', where: 'Acts 28', when: 'c. AD 60', era: 'church' },
  { id: 'temple-destroyed', title: 'Rome destroys the temple', where: 'History', when: 'AD 70', era: 'church' },
  { id: 'revelation', title: 'The revelation on Patmos', where: 'Revelation 1', when: 'c. AD 95', era: 'church' },
];

/** Order is position in the list, so inserting an event renumbers nothing. */
export const TIMELINE = SEQUENCE.map((event, index) => ({ ...event, order: index }));

export const TIMELINE_BY_ID = Object.fromEntries(TIMELINE.map((e) => [e.id, e]));

export const ERAS = {
  genesis: 'Before the nation',
  patriarchs: 'The patriarchs',
  exodus: 'Out of Egypt',
  land: 'The land',
  kings: 'The kings',
  exile: 'Exile',
  return: 'The return',
  gospels: 'The gospels',
  church: 'The church',
};
